import { DAY } from './format';
import { MONTH_DAYS } from './package';
import { poTotals, type PoLine, type PurchaseOrder } from './po';

/**
 * Chiria reală: cofrajele și schelele se plătesc pe zilele în care stau pe
 * șantier, nu pe zilele scrise în cerere. Ceri 45, stau 62 → plătești 62; stau
 * 20 cu chirie minimă 30 → plătești 30. Zilele se numără de la livrare până la
 * retur, **fără ziua returului** (1 octombrie → 1 decembrie = 61 de zile), cum
 * facturează furnizorii (decizia proprietarului, 8 octombrie).
 *
 * Comanda emisă rămâne cum a fost trimisă; aici se socotește doar costul.
 */

/** Zilele dintre două date 'AAAA-LL-ZZ', fără ziua de sfârșit. */
export function daysBetween(from: string, to: string): number {
  const a = new Date(`${from}T12:00:00Z`).getTime();
  const b = new Date(`${to}T12:00:00Z`).getTime();
  return Math.max(0, Math.round((b - a) / DAY));
}

export interface RentalUse {
  /** 'AAAA-LL-ZZ': prima livrare de pe aviz */
  from: string;
  /** 'AAAA-LL-ZZ': ultimul retur; null = încă pe șantier */
  to: string | null;
  /** zilele pe șantier până la retur (sau până azi) */
  days: number;
  /** zilele plătite: cel puțin chiria minimă */
  billedDays: number;
  minDays: number | null;
}

const isRent = (l: PoLine) => l.rent != null;

/** Tariful pe zi al unei poziții de chirie: 'lună' = 30 de zile. */
const perDay = (l: PoLine) => (l.unit.trim().toLowerCase() === 'lună' ? l.unitPrice / MONTH_DAYS : l.unitPrice);

/**
 * Cât a stat chiria pe șantier, din avize. null dacă comanda n-are poziții de
 * chirie sau marfa n-a fost livrată încă (atunci rămâne estimarea de pe comandă).
 */
export function rentalUse(
  o: Pick<PurchaseOrder, 'data' | 'deliveries'>,
  today: string,
): RentalUse | null {
  if (!o.data.lines.some(isRent)) return null;
  const ins = o.deliveries.filter((d) => d.kind === 'in').map((d) => d.date).sort();
  const from = ins[0];
  if (!from) return null;
  const back = o.deliveries
    .filter((d) => d.kind === 'return' && d.date >= from)
    .map((d) => d.date)
    .sort();
  const to = back.length ? back[back.length - 1]! : null;
  const days = daysBetween(from, to ?? today);
  const mins = o.data.lines.filter(isRent).map((l) => l.rent!.minDays ?? 0);
  const minDays = Math.max(0, ...mins) || null;
  return { from, to, days, billedDays: Math.max(days, minDays ?? 0), minDays };
}

export interface OrderValue {
  /** valoarea fără TVA, în lei */
  lei: number;
  /** chiria socotită din avize; null = valoarea de pe comandă */
  rental: RentalUse | null;
  /** chiria e încă pe șantier: valoarea crește în fiecare zi */
  running: boolean;
}

/**
 * Valoarea unei comenzi: cea de pe document sau, la chirie livrată, chiria pe
 * zilele reale (până la retur sau până azi) plus restul pozițiilor, transportul
 * și costurile ei.
 */
export function orderValue(o: Pick<PurchaseOrder, 'data' | 'deliveries' | 'netLei'>, today: string): OrderValue {
  const use = rentalUse(o, today);
  if (!use) return { lei: o.netLei, rental: null, running: false };
  const lines = o.data.lines.map((l) =>
    isRent(l) ? { ...l, unit: 'zi', unitPrice: perDay(l), qty: use.billedDays } : l,
  );
  const net = poTotals({ ...o.data, lines }).net;
  const fx = o.data.currency === 'EUR' ? (o.data.fxRate ?? 1) : 1;
  return { lei: Math.round(net * fx * 100) / 100, rental: use, running: use.to == null };
}
